// ============================================================
// EffectList - the effects of one stance (or the always-on list), editable.
//
// A row per effect: its fields, what it says in a sentence, and a remove
// button; then "add" - pick a kind, get a blank one to adjust. An exposure
// is not typed in: it is picked with the same picker the Status tab uses,
// and stored with no clock (a stance's exposure lasts as long as the
// stance does).
//
// Which fields a kind has is a lookup (`FIELDS`), so a new kind is a row
// there and a row in `engine/character/effectChoices`.
//
// An effect applied to an attack (a bonus to WS or BS, or to damage) may
// also carry a condition - `WhenEditor`.
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { EFFECT_CHOICES, SKILL_CHOICES, blankEffect } from '../../engine/character/effectChoices';
import type { When } from '../../engine/character/effectWhen';
import { effectSentence, isSheetKind, takesWhen } from '../../engine/character/effectItems';
import { isSelfOnly } from '../../engine/character/effectWhen';
import type { EffectKind, StanceEffect } from '../../engine/character/effectItems';
import { buildExposureCode, parseExposure } from '../../engine/exposures';
import AddExposureModal from '../AddExposureModal';
import WhenEditor from './WhenEditor';
import { Amount, Pick } from './effectFieldParts';
import { SHEET_FIELDS } from './SheetEffectFields';
import common from '../../styles/common.module.css';
import cards from '../../styles/Submenus.module.css';

interface Props {
  effects: StanceEffect[];
  onChange: (next: StanceEffect[]) => void;
  /** Names the list for assistive tech and prefixes its test ids: "always-on", or a stance's id. */
  id: string;
  /** What the list belongs to, for the controls' accessible names: "Assault", "Always on". */
  owner: string;
  locked?: boolean;
}

type Of<K extends EffectKind> = Extract<StanceEffect, { type: K }>;
type FieldProps<K extends EffectKind> = { effect: Of<K>; set: (next: Of<K>) => void; label: string; locked: boolean };

const SCOPES = [
  { id: 'all', label: 'any attack' },
  { id: 'melee', label: 'in melee' },
  { id: 'ranged', label: 'at range' },
] as const;
const REROLLS = [
  { id: 'damage', label: 'the damage' },
  { id: 'attack', label: 'a missed attack' },
] as const;
const DEFENCES = [
  { id: 'dodge', label: 'Dodge' },
  { id: 'parry', label: 'Parry' },
] as const;

type Scoped = Of<'damage_bonus'> | Of<'called_shot_reduction'>;

/** A scope and an amount: the two kinds that are "so much, on this kind of attack". */
function scopedAmount<E extends Scoped>(min?: number) {
  return function ScopedAmount({
    effect,
    set,
    label,
    locked,
  }: {
    effect: E;
    set: (next: E) => void;
    label: string;
    locked: boolean;
  }) {
    return (
      <>
        <Amount
          value={effect.value}
          onChange={(value) => set({ ...effect, value })}
          label={`${label}: amount`}
          locked={locked}
          min={min}
        />
        <Pick
          value={effect.scope}
          options={SCOPES}
          onPick={(scope) => set({ ...effect, scope })}
          label={`${label}: on`}
          locked={locked}
        />
      </>
    );
  };
}

/** The fields of each kind. An exposure has none: it is replaced by picking another. */
const FIELDS: { [K in EffectKind]: (p: FieldProps<K>) => ReactNode } = {
  // The sheet tier (plan armoury): characteristics, grants, wounds, armour, resistance.
  ...SHEET_FIELDS,
  skill_bonus: ({ effect, set, label, locked }) => (
    <>
      <Pick
        value={effect.skill ?? 'ws'}
        options={SKILL_CHOICES}
        onPick={(skill) => set({ ...effect, skill })}
        label={`${label}: skill`}
        locked={locked}
      />
      <Amount
        value={effect.value}
        onChange={(value) => set({ ...effect, value })}
        label={`${label}: amount`}
        locked={locked}
      />
    </>
  ),
  damage_bonus: scopedAmount<Of<'damage_bonus'>>(),
  // A reduction is never negative: it would be a penalty with another name.
  called_shot_reduction: scopedAmount<Of<'called_shot_reduction'>>(0),
  defence_grant: ({ effect, set, label, locked }) => (
    <>
      <Amount
        value={effect.additional}
        onChange={(additional) => set({ ...effect, additional })}
        label={`${label}: how many`}
        locked={locked}
        min={1}
      />
      <Pick
        value={effect.defence}
        options={DEFENCES}
        onPick={(defence) => set({ ...effect, defence })}
        label={`${label}: which`}
        locked={locked}
      />
    </>
  ),
  initiative_modifier: ({ effect, set, label, locked }) => (
    <Amount
      value={effect.value}
      onChange={(value) => set({ ...effect, value })}
      label={`${label}: amount`}
      locked={locked}
    />
  ),
  exposure: () => null,
  attack_reroll: ({ effect, set, label, locked }) => (
    <Pick
      value={effect.of}
      options={REROLLS}
      onPick={(of) => set({ ...effect, of })}
      label={`${label}: of`}
      locked={locked}
    />
  ),
};

/** The effect with this condition, or with none: the key is absent, never `undefined`, on the wire. */
function conditionedBy(effect: StanceEffect, when: When | undefined): StanceEffect {
  const { when: _was, ...rest } = effect;
  return (when ? { ...rest, when } : rest) as StanceEffect;
}

/** An edit may leave an effect that cannot carry a condition (a bonus moved to another skill): the condition goes. */
const fitting = (effect: StanceEffect): StanceEffect => (fits(effect) ? effect : conditionedBy(effect, undefined));

/** Can this effect keep its condition? An attack effect any; a sheet effect one about oneself. */
const fits = (effect: StanceEffect): boolean =>
  !effect.when || takesWhen(effect) || (isSheetKind(effect.type) && isSelfOnly(effect.when));

/** A stance's exposure has no clock of its own: it lasts as long as the stance. */
function timeless(code: string): string {
  const ex = parseExposure(code);
  return ex ? buildExposureCode({ ...ex, rounds: 0 }) : code;
}

function EffectRow(props: {
  effect: StanceEffect;
  label: string;
  testId: string;
  locked: boolean;
  onSet: (next: StanceEffect) => void;
  onRemove: () => void;
}) {
  const { effect, label, locked } = props;
  const Fields = FIELDS[effect.type] as (p: FieldProps<EffectKind>) => ReactNode;
  return (
    // A card (plan armoury, part 6): what it does on top, its parts below, its condition in place.
    <li className={cards.row} data-testid={props.testId}>
      <div className={cards.rowHead}>
        <b className={cards.rowSay}>{effectSentence(effect)}</b>
        <button
          type="button"
          className={cards.iconButton}
          aria-label={`Remove ${label}`}
          disabled={locked}
          onClick={props.onRemove}
        >
          {'✕'}
        </button>
      </div>
      <div className={cards.rowParts}>
        <Fields effect={effect} set={(next) => props.onSet(fitting(next))} label={label} locked={locked} />
      </div>
      {(takesWhen(effect) || isSheetKind(effect.type)) && (
        <WhenEditor
          when={effect.when}
          onChange={(when) => props.onSet(conditionedBy(effect, when))}
          label={label}
          locked={locked}
          // A sheet effect waits only on oneself (the two tiers): the editor offers nothing else.
          selfOnly={!takesWhen(effect)}
        />
      )}
    </li>
  );
}

export default function EffectList({ effects, onChange, id, owner, locked = false }: Props) {
  const [picking, setPicking] = useState(false);
  const add = (kind: EffectKind | '') => {
    if (kind === 'exposure') setPicking(true);
    else if (kind) onChange([...effects, blankEffect(kind)]);
  };
  const replace = (i: number, next: StanceEffect) => onChange(effects.map((e, j) => (j === i ? next : e)));
  return (
    <>
      <ul className={cards.rows} aria-label={`Effects of ${owner}`}>
        {effects.map((effect, i) => (
          <EffectRow
            key={i}
            effect={effect}
            label={`${owner}, effect ${i + 1}`}
            testId={`effect-${id}-${i}`}
            locked={locked}
            onSet={(next) => replace(i, next)}
            onRemove={() => onChange(effects.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
      {/* Always back on its caption: choosing a kind adds one, it is not a setting. */}
      <select
        className={common.input}
        aria-label={`Add an effect to ${owner}`}
        data-testid={`effect-add-${id}`}
        value=""
        disabled={locked}
        onChange={(e) => add(e.target.value as EffectKind | '')}
      >
        <option value="">+ Add an effect…</option>
        {EFFECT_CHOICES.map((c) => (
          <option key={c.kind} value={c.kind}>
            {c.label}
          </option>
        ))}
      </select>
      {picking && (
        <AddExposureModal
          onAdd={(code: string) => onChange([...effects, { type: 'exposure', code: timeless(code) }])}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}
